import { cloneHeights, isSummit, targetSummit } from './board.js';
import { RuleError } from './errors.js';
import { positionKey } from './hash.js';
import { actionSeenBy, resolveIntent, updateMemory, viewFor } from './fog.js';
import { hasLegalAction, isPinned, legalActions, sameAction } from './moves.js';
import { toNotation } from './notation.js';
import { otherTeam } from './team.js';
import type { Action, GameState, LogEntry, Piece, Team, WinReason } from './types.js';

export function isGameOver(state: GameState): boolean {
  return state.winReason !== null;
}

/**
 * Applies one action and returns the new state. The input state is never modified.
 * Throws a `RuleError` if the action is not legal.
 *
 * After the action, the game end is checked in this order: capture of the enemy Beacon,
 * then (after the turn passes) the new mover's Beacon on the summit (not with home summits), then the new mover having
 * no legal action. Optional draws (repetition, move limit) are checked last.
 */
export function applyAction(state: GameState, pieceId: string, action: Action): GameState {
  const { piece, actions } = checkedActions(state, pieceId);
  const intended = actions.find((a) => sameAction(a, action));
  if (intended === undefined) {
    throw new RuleError(
      'ILLEGAL_ACTION',
      `Illegal action for ${pieceId}: ${JSON.stringify(action)}`,
    );
  }
  // Under fog the player chose from their view; the real board decides what happens.
  const legal = state.config.fog ? resolveIntent(state, piece, intended) : intended;

  const notation = toNotation(state, pieceId, intended);
  const { pieces, heights, captured } = transform(state, pieceId, legal);

  const entry: LogEntry = {
    ply: state.ply,
    moveNo: state.moveNo,
    team: state.turn,
    pieceId,
    action: intended,
    notation,
    ...(captured === undefined ? {} : { captured }),
    ...(legal === null
      ? { blocked: true as const }
      : sameAction(legal, intended)
        ? {}
        : { result: legal }),
  };
  const ply = state.ply + 1;
  const next: GameState = {
    ...state,
    heights,
    pieces,
    turn: otherTeam(state.turn),
    ply,
    moveNo: Math.floor(ply / 2) + 1,
    log: [...state.log, entry],
    positions: { ...state.positions },
  };
  if (state.memory !== null) {
    const revealed = legal === null ? { team: state.turn, r: intended.r, c: intended.c } : null;
    next.memory = updateMemory(next, state.memory, revealed);
    if (!actionSeenBy(state, next, next.turn, pieceId, intended)) entry.hiddenFrom = next.turn;
  }
  const key = positionKey(next);
  const seen = (next.positions[key] ?? 0) + 1;
  next.positions[key] = seen;
  return settle(state, next, captured, seen);
}

/**
 * Applies an action that is already known to be legal, for bots searching ahead. It skips
 * validation, notation, the log, the repetition table and fog of war, but changes the board and
 * ends the game exactly like `applyAction` (except the optional repetition draw, which needs the
 * table). Never use it for real games.
 */
export function applyTrusted(state: GameState, pieceId: string, action: Action): GameState {
  const { pieces, heights, captured } = transform(state, pieceId, action);
  const ply = state.ply + 1;
  const next: GameState = {
    ...state,
    heights,
    pieces,
    turn: otherTeam(state.turn),
    ply,
    moveNo: Math.floor(ply / 2) + 1,
  };
  return settle(state, next, captured, null);
}

/** The pieces and terrain after `action` (or unchanged when it was blocked under fog). */
function transform(
  state: GameState,
  pieceId: string,
  legal: Action | null,
): { pieces: Piece[]; heights: number[][]; captured: string | undefined } {
  const pieces = state.pieces.map((p) => ({ ...p }));
  let heights = state.heights;
  let captured: string | undefined;

  const mover = pieces.find((p) => p.id === pieceId);
  /* c8 ignore next */
  if (mover === undefined) throw new Error('unreachable');
  switch (legal?.kind) {
    case undefined:
      break;
    case 'move':
      mover.r = legal.r;
      mover.c = legal.c;
      break;
    case 'capture': {
      const target = pieces.find((p) => p.id === legal.target);
      /* c8 ignore next */
      if (target === undefined) throw new Error('unreachable');
      target.alive = false;
      captured = target.id;
      mover.r = legal.r;
      mover.c = legal.c;
      break;
    }
    case 'pin': {
      const target = pieces.find((p) => p.id === legal.pin);
      /* c8 ignore next */
      if (target === undefined) throw new Error('unreachable');
      target.pinnedAt = state.ply;
      break;
    }
    case 'build': {
      heights = cloneHeights(heights);
      const row = heights[legal.r];
      /* c8 ignore next */
      if (row === undefined) throw new Error('unreachable');
      row[legal.c] = (row[legal.c] ?? 0) + legal.delta;
      break;
    }
  }
  return { pieces, heights, captured };
}

/**
 * Game-end checks after an action, in rule order: capture of the enemy Beacon, the new mover's
 * Beacon on the summit, the new mover having no legal action, then the optional draws.
 * `seen` is how often this position has occurred, or `null` when it is not tracked.
 */
function settle(
  state: GameState,
  next: GameState,
  captured: string | undefined,
  seen: number | null,
): GameState {
  const { pieces } = next;
  const capturedBeacon =
    captured !== undefined && pieces.some((p) => p.id === captured && p.type === 'B');
  if (capturedBeacon) return end(next, state.turn, 'capture');

  if (next.config.summitMode === 'twin') {
    const mover = pieces.find((p) => p.alive && p.type === 'B' && p.team === state.turn);
    const target = targetSummit('twin', state.turn);
    if (mover !== undefined && mover.r === target.r && mover.c === target.c) {
      return end(next, state.turn, 'summit');
    }
    return finish(state, next, seen);
  }
  // Home summits are refuges, not goals: only a capture (or a stuck opponent) wins.
  if (next.config.summitMode === 'home') return finish(state, next, seen);

  const beacon = pieces.find((p) => p.alive && p.type === 'B' && p.team === next.turn);
  const onSummit = beacon !== undefined && isSummit(beacon.r, beacon.c);
  const streak = onSummit ? state.summitStreak[next.turn] + 1 : 0;
  next.summitStreak = { ...state.summitStreak, [next.turn]: streak };
  if (beacon !== undefined && onSummit && streak >= next.config.summitHold) {
    const contested =
      next.config.summitContested &&
      pieces.some(
        (p) =>
          p.alive &&
          p.team === state.turn &&
          Math.max(Math.abs(p.r - beacon.r), Math.abs(p.c - beacon.c)) === 1,
      );
    if (!contested) return end(next, next.turn, 'summit');
  }
  return finish(state, next, seen);
}

/** The checks after the summit: the new mover stuck, then the optional draws. */
function finish(state: GameState, next: GameState, seen: number | null): GameState {
  const { ply } = next;
  if (!hasLegalAction(next)) return end(next, state.turn, 'stuck');

  if (next.config.threefoldRepetition && seen !== null && seen >= 3) {
    return end(next, null, 'repetition');
  }
  if (next.config.moveLimit !== null && ply >= next.config.moveLimit * 2) {
    return end(next, null, 'move-limit');
  }
  return next;
}

/**
 * A piece and the actions its player may choose (from their view under fog), or a `RuleError`
 * explaining why it cannot act.
 */
function checkedActions(state: GameState, pieceId: string): { piece: Piece; actions: Action[] } {
  if (isGameOver(state)) throw new RuleError('GAME_OVER', 'The game is over');
  const piece = state.pieces.find((p) => p.id === pieceId);
  if (piece === undefined) throw new RuleError('UNKNOWN_PIECE', `No piece with id ${pieceId}`);
  if (!piece.alive) throw new RuleError('PIECE_CAPTURED', `${pieceId} has been captured`);
  if (piece.team !== state.turn)
    throw new RuleError('NOT_YOUR_TURN', `It is not ${piece.team}'s turn`);
  if (isPinned(state, piece)) throw new RuleError('PINNED', `${pieceId} is pinned this turn`);
  const actions = legalActions(state.config.fog ? viewFor(state, state.turn) : state, pieceId);
  return { piece, actions };
}

function end(state: GameState, winner: Team | null, reason: WinReason): GameState {
  return { ...state, winner, winReason: reason };
}

function assertRunning(state: GameState): void {
  if (isGameOver(state)) throw new RuleError('GAME_OVER', 'The game is over');
}

/** `team` resigns; the other team wins. */
export function resign(state: GameState, team: Team): GameState {
  assertRunning(state);
  return end(state, otherTeam(team), 'resign');
}

/** `team` ran out of time; the other team wins. Clocks are owned by the server. */
export function timeout(state: GameState, team: Team): GameState {
  assertRunning(state);
  return end(state, otherTeam(team), 'timeout');
}

/** Both players agreed to a draw. */
export function agreeDraw(state: GameState): GameState {
  assertRunning(state);
  return end(state, null, 'agreement');
}
